import { useCallback, useState, useMemo } from 'react';
import { Box, Grid, Container, Stack, Button, Typography, Tabs, Tab } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
// import { TabContext, TabPanel, TabList } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import BrudCrumbs from '../../components/BreadCrumbs';
import CustomInput from '../../components/CustomInput';
import { saveApiData, savefileApiData } from '../../store/Slice/Global/GlobalSlice';
import { DataSourceApi } from '../../utils/Apis';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import CustomSelect from '../../components/CustomSelect';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';

const STATIC_TABS = [
  { id: 1, label: 'Add New' },
  { id: 2, label: 'Append' },
  { id: 3, label: 'Rewrite' }
];

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

function FileUploadSection({ control, setValue, fileValue, errors }) {
  return (
    <Controller
      name="file"
      control={control}
      rules={{ required: 'File is required' }}
      render={() => (
        <Box
          sx={{
            border: '2px dashed #4fbbc8',
            borderRadius: '12px',
            p: 4,
            textAlign: 'center',
            bgcolor: '#f7fcff'
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 60, color: '#4fbbc8', mb: 1 }} />

          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Upload File
          </Typography>

          <Typography variant="body2" sx={{ mt: 1 }}>
            Allowed: .xls, .xlsx, .csv — Max 10MB
          </Typography>

          <Button component="label" variant="outlined" sx={{ mt: 2 }}>
            Choose File
            <VisuallyHiddenInput
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setValue('file', file, { shouldValidate: true });
              }}
            />
          </Button>

          {fileValue && <Typography mt={1}>{fileValue.name}</Typography>}

          {errors?.file && (
            <Typography color="error" mt={1}>
              {errors.file.message}
            </Typography>
          )}
        </Box>
      )}
    />
  );
}

FileUploadSection.propTypes = {
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  fileValue: PropTypes.object,
  errors: PropTypes.object
};

export default function DataAdd() {
  const [fileTypeId, setFileTypeId] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const { DataSourceListData } = useDataSourceSelector();

  const token = LoginData?.jwt;
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      datasetName: '',
      datasetDescription: '',
      file: null
    }
  });

  const handleChangeTab = useCallback(
    (_, newValue) => {
      setFileTypeId(newValue);
      reset({
        datasetName: '',
        datasetDescription: '',
        file: null
      });
    },
    [reset]
  );

  const fileValue = watch('file');

  const onSubmit = useCallback(
    async (data) => {
      const formData = new FormData();

      // Decide purpose based on selected tab
      let purpose = '';
      if (fileTypeId === 1) purpose = 'ADD_NEW';
      if (fileTypeId === 2) purpose = 'APPEND';
      if (fileTypeId === 3) purpose = 'REWRITE';

      formData.append('purpose', purpose);

      // ---------- TAB-1 : ADD NEW ----------
      if (fileTypeId === 1) {
        formData.append('datasetName', data.datasetName);
        formData.append('datasetDescription', data.datasetDescription || '');
      }

      // ---------- TAB-2 / TAB-3 : APPEND / REWRITE ----------
      if (fileTypeId === 2 || fileTypeId === 3) {
        const selected = DataSourceListData?.find((x) => x.id === data.datasetName);

        formData.append('datasetId', data.datasetName); // ✅ correct key
        formData.append('datasetName', selected?.datasetName); // ✅ same as curl

        if (fileTypeId === 3) {
          formData.append('datasetDescription', data.datasetDescription || '');
        }
      }

      // ---------- FILE ----------
      if (data.file) {
        formData.append('file', data.file);
      }

      try {
        const res = await dispatch(
          savefileApiData(`${DataSourceApi?.dataSourceUpload}`, formData, token)
        );

        if (res?.success === true) navigate('/data');
      } catch (err) {
        console.error('Submit failed:', err);
      }
    },
    [dispatch, navigate, fileTypeId, DataSourceListData, token]
  );

  const selectOptions = useMemo(
    () =>
      DataSourceListData?.map((item) => ({
        label: item.datasetName,
        value: item.id
      })) || [],
    [DataSourceListData]
  );
  return (
    <Page title="Partner Management System || Add Data Source">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Add Data Source" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Tabs
            value={fileTypeId}
            onChange={handleChangeTab}
            className="tabOuter"
            classes={{ indicator: 'tabIndicator' }}
            style={{ minHeight: '75px' }}
          >
            {STATIC_TABS.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.label}
                classes={{ selected: 'tabSelected', root: 'tabDefault' }}
              />
            ))}
          </Tabs>
          {/* -------- TAB 1 : ADD NEW -------- */}
          <TabPanel value={fileTypeId} index={1} style={{ paddingBottom: '100px' }}>
            {/* put your Dataset Information form here */}{' '}
            <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
              <form id="addDataSourceForm" onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <CustomInput
                          name="datasetName"
                          label="Dataset Name"
                          control={control}
                          errors={errors}
                          isRequired
                          fieldType="TEXT_FIELD"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <CustomInput
                          name="datasetDescription"
                          label="Dataset Description"
                          type="text"
                          control={control}
                          errors={errors}
                          isRequired
                          fieldType="TEXT_FIELD"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={6}>
                    <FileUploadSection
                      control={control}
                      setValue={setValue}
                      fileValue={fileValue}
                      errors={errors}
                    />
                  </Grid>

                  <Grid item xs={12} textAlign="right">
                    <Button type="submit" variant="contained" className="mainBtn">
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>{' '}
          </TabPanel>

          {/* -------- TAB 2 : APPEND -------- */}
          <TabPanel value={fileTypeId} index={2} style={{ paddingBottom: '100px' }}>
            {/* append form */}{' '}
            <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
              <form id="addDataSourceForm" onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <CustomSelect
                          name="datasetName"
                          label="Dataset Name"
                          control={control}
                          errors={errors}
                          options={selectOptions || []}
                          isRequired
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={6}>
                    <FileUploadSection
                      control={control}
                      setValue={setValue}
                      fileValue={fileValue}
                      errors={errors}
                    />
                  </Grid>

                  <Grid item xs={12} textAlign="right">
                    <Button type="submit" variant="contained" className="mainBtn">
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>{' '}
          </TabPanel>

          {/* -------- TAB 3 : REWRITE -------- */}
          <TabPanel value={fileTypeId} index={3} style={{ paddingBottom: '100px' }}>
            {/* rewrite form */}{' '}
            <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
              <form id="addDataSourceForm" onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <CustomSelect
                          name="datasetName"
                          label="Dataset Name"
                          control={control}
                          errors={errors}
                          options={selectOptions || []}
                          isRequired
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <CustomInput
                          name="datasetDescription"
                          label="Dataset Description"
                          type="text"
                          control={control}
                          errors={errors}
                          isRequired
                          fieldType="TEXT_FIELD"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={6}>
                    <FileUploadSection
                      control={control}
                      setValue={setValue}
                      fileValue={fileValue}
                      errors={errors}
                    />
                  </Grid>

                  <Grid item xs={12} textAlign="right">
                    <Button type="submit" variant="contained" className="mainBtn">
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>{' '}
          </TabPanel>
        </Box>
      </Container>
    </Page>
  );
}

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box className="tab-panel-box">{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};
